import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    FiLogOut,
    FiChevronsLeft,
    FiChevronsRight,
} from 'react-icons/fi';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useAuth } from '../../auth/AuthContext';
import { useToast } from '../../theme/components';
import './SideBar.css';
import { getSideBarEntries } from './SideBarData';
import { APP_NAME } from '../../config';

const SideBar = ({ collapsed = false, onToggleCollapsed, onNavigate }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const toast = useToast();
    const { items: menuItems, adminDivider } = getSideBarEntries(user?.role);

    const initials = (user?.fullName || user?.email || '?')
        .split(/\s+|@/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');

    const handleLogout = () => {
        logout();
        toast.success('Signed out.');
        navigate('/login', { replace: true });
    };

    const handleNavClick = () => {
        if (typeof onNavigate === 'function') onNavigate();
    };

    // Sub-component so the menu map stays tidy.
    const SidebarLink = ({ item, active, collapsed, onNavigate }) => {
        const link = (
            <Link
                to={item.path}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
            >
                <span className="sidebar-icon">{item.icon}</span>
                <span className="sidebar-title">{item.title}</span>
            </Link>
        );
        if (collapsed) {
            return (
                <Tooltip title={item.title} placement="right">
                    <span className="sidebar-link-wrap">{link}</span>
                </Tooltip>
            );
        }
        return link;
    };

    return (
        <nav className={`sidebar ${collapsed ? 'is-collapsed' : ''}`} aria-label="Main">
            <div className="sidebar-header">
                {!collapsed && (
                    <span className="sidebar-appname">{APP_NAME}</span>
                )}
                {!collapsed && typeof onToggleCollapsed === 'function' && (
                    <Tooltip title="Collapse sidebar" placement="right">
                        <IconButton
                            size="small"
                            className="sidebar-collapse-btn"
                            onClick={onToggleCollapsed}
                            aria-label="Collapse sidebar"
                        >
                            <FiChevronsLeft size={16} />
                        </IconButton>
                    </Tooltip>
                )}
                {collapsed && typeof onToggleCollapsed === 'function' && (
                    <Tooltip title="Expand sidebar" placement="right">
                        <IconButton
                            size="small"
                            className="sidebar-collapse-btn sidebar-collapse-btn--collapsed"
                            onClick={onToggleCollapsed}
                            aria-label="Expand sidebar"
                        >
                            <FiChevronsRight size={16} />
                        </IconButton>
                    </Tooltip>
                )}
            </div>

            <ul className="sidebar-menu">
                {menuItems.map((item, idx) => {
                    const showDivider = adminDivider && idx === 6; // after the 6 default items
                    if (showDivider) {
                        return (
                            <React.Fragment key={`divider-${item.title}`}>
                                <li className="sidebar-divider" aria-hidden="true" />
                                <li
                                    className={`sidebar-item ${
                                        location.pathname === item.path ? 'active' : ''
                                    }`}
                                >
                                    <SidebarLink
                                        item={item}
                                        active={location.pathname === item.path}
                                        collapsed={collapsed}
                                        onNavigate={handleNavClick}
                                    />
                                </li>
                            </React.Fragment>
                        );
                    }
                    return (
                        <li
                            key={item.title}
                            className={`sidebar-item ${
                                location.pathname === item.path ? 'active' : ''
                            }`}
                        >
                            <SidebarLink
                                item={item}
                                active={location.pathname === item.path}
                                collapsed={collapsed}
                                onNavigate={handleNavClick}
                            />
                        </li>
                    );
                })}
            </ul>

            {user && (
                <>
                    <div className="sidebar-user-card" aria-label="Signed-in user">
                        <div className="sidebar-user-avatar" aria-hidden="true">
                            {initials || '?'}
                        </div>
                        {!collapsed && (
                            <div className="sidebar-user-info">
                                <span
                                    className="sidebar-user-name"
                                    title={user.fullName || user.email}
                                >
                                    {user.fullName || user.email}
                                </span>
                                <span className="sidebar-user-email" title={user.email}>
                                    {user.email}
                                </span>
                                <span
                                    className={`sidebar-user-role sidebar-user-role--${
                                        user.role || 'user'
                                    }`}
                                >
                                    {user.role || 'user'}
                                </span>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className="sidebar-signout"
                        onClick={handleLogout}
                        aria-label="Sign out"
                    >
                        <FiLogOut size={16} aria-hidden="true" />
                        {!collapsed && (
                            <span className="sidebar-signout__label">Sign out</span>
                        )}
                    </button>
                </>
            )}
        </nav>
    );
};

export default SideBar;
